import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AuditHistoryItem, AuditHistoryProps, AuditHistoryResponse } from '../types/audit.types';

export interface UseAuditHistoryProps {
  fetchFn: AuditHistoryProps['fetchFn'];
  entityId: string;
  pageSize?: number;
}

export interface UseAuditHistoryResult {
  data: AuditHistoryResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  page: number;
  setPage: (page: number) => void;
  selectedEntryId: string | null;
  setSelectedEntryId: (id: string | null) => void;
  selectedEntry: AuditHistoryItem | undefined;
}

export function useAuditHistory({
  fetchFn,
  entityId,
  pageSize = 20,
}: UseAuditHistoryProps): UseAuditHistoryResult {
  const [page, setPageState] = useState(1);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['audit-history', entityId, page, pageSize],
    queryFn: () => fetchFn(entityId, { page, pageSize }),
    enabled: !!entityId,
    staleTime: 30_000,
    gcTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Auto-selecciona la primera entrada cuando llegan nuevos datos
  useEffect(() => {
    if (data?.items && data.items.length > 0) {
      setSelectedEntryId(data.items[0].id);
    } else {
      setSelectedEntryId(null);
    }
  }, [data]);

  const setPage = (newPage: number) => {
    setSelectedEntryId(null);
    setPageState(newPage);
  };

  const selectedEntry = data?.items.find((item) => item.id === selectedEntryId);

  return {
    data,
    isLoading,
    isError,
    page,
    setPage,
    selectedEntryId,
    setSelectedEntryId,
    selectedEntry,
  };
}
